import { parse as parseCsv } from "papaparse";
import type { PaginationOptions } from "../domain/pagination";
import type {
  ExportTableDataResponse,
  ImportTableDataFromAPIInput,
  ImportTableDataFromFileInput,
  TableDatumInput,
} from "../domain/table-data";
import { type ApiRequestResponse, fetchQuery } from "../shared/http-client";
import {
  type TableData,
  formatFetchTableDataAPI,
  formatToExportTableDataResponse,
} from "./table-data.formatter";

export async function searchTableDataAPI({
  referenceId,
  tableId,
  searchQueries,
  options: { limit, page },
}: {
  referenceId: number;
  tableId: number;
  searchQueries: TableDatumInput;
  options: PaginationOptions;
}): Promise<ApiRequestResponse<TableData[]>> {
  const filters: Record<string, { operator: string; value: string }> = {};
  searchQueries.forEach((value, key) => {
    filters[key] = {
      operator: "ilike",
      value: `%${value}%`,
    };
  });

  const res = await fetchQuery(
    `references/${referenceId}/tables/${tableId}/data/search?limit=${limit}&page=${page}`,
    {
      method: "POST",
      body: JSON.stringify({
        filters,
      }),
    },
  );
  if (!res)
    return {
      data: [],
    };

  return {
    data: formatFetchTableDataAPI(res.data),
    pagination: res.pagination,
  };
}

export async function fetchTableDataAPI({
  referenceId,
  tableId,
}: { referenceId: number; tableId: number }): Promise<TableData[]> {
  const res = await fetchQuery(`references/${referenceId}/tables/${tableId}/data`);
  if (!res.data) return [];
  return formatFetchTableDataAPI(res.data);
}

export async function fetchTableDatumAPI({
  referenceId,
  tableId,
  datumId,
}: { referenceId: number; tableId: number; datumId: number }): Promise<TableData | undefined> {
  const res = await fetchQuery(`references/${referenceId}/tables/${tableId}/data/${datumId}`);
  if (!res.data) return;
  return formatFetchTableDataAPI(res.data)?.[0];
}

export async function importTableDataAPI({
  referenceId,
  tableId,
  input,
}: { referenceId: number; tableId: number; input: ImportTableDataFromAPIInput }): Promise<{success: boolean; message: string}> {
  const response = await fetchQuery<{success: boolean; message: string}>(`references/${referenceId}/tables/${tableId}/data/import-api`, {
    method: "POST",
    body: JSON.stringify({
      api_url: input.apiUrl,
      http_method: input.method,
      auth_type: input.authType,
      api_key: input.apiToken,
      headers: input.headers,
      columns: input.columnsMapping,
    }),
  });
  if (!response) {
    return { success: false, message: "No response from server" };
  }
  return { success: response.data.success, message: response.data.message };
}

export async function exportTableDataAPI({
  referenceId,
  tableId,
}: { referenceId: number; tableId: number }): Promise<ExportTableDataResponse> {
  const res = await fetchQuery(`references/${referenceId}/tables/${tableId}/data/export`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  if (!res) {
    return {
      success: false,
    };
  }
  return formatToExportTableDataResponse(res.data);
}

export async function importTableDataFromFileAPI({
  referenceId,
  tableId,
  input,
}: {
  referenceId: number;
  tableId: number;
  input: ImportTableDataFromFileInput;
}): Promise<{ success: boolean; message: string }> {
  let headerColumns: string[] = [];
  const formData = new FormData();
  if (input.file) {
    headerColumns = await readFileHeaderColumns(input.file);
    formData.append("file", input.file);
  }
  for (const [key, value] of Object.entries(input.columnsMapping || {})) {
    const valueIndex = headerColumns.findIndex(
      (col) =>
        col.trim().toLowerCase().replace(",", "") === value.trim().toLowerCase()
    );
    if (valueIndex === -1) {
      throw new Error(`Column "${value}" not found in file`);
    }
    formData.append(`columns[${key}]`, valueIndex.toString());
  }
  try {
    const response = await fetchQuery<{ success: boolean; message: string }>(
      `references/${referenceId}/tables/${tableId}/data/import`,
      {
        method: "POST",
        body: formData,
      }
    );
    if (!response) {
      return { success: false, message: "No response from server" };
    }
    return { success: response.data.success, message: response.data.message };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Erreur inconnue",
    };
  }
}

export async function createTableDataAPI({
  referenceId,
  tableId,
  input,
}: { referenceId: number; tableId: number; input: TableDatumInput | FormData }): Promise<void> {
  await fetchQuery(`references/${referenceId}/tables/${tableId}/data`, {
    method: "POST",
    body: convertToJsonIfNotFormData(input),
  });
}

export async function updateTableDataAPI({
  referenceId,
  tableId,
  datumId,
  input,
}: {
  referenceId: number;
  tableId: number;
  datumId: number;
  input: TableDatumInput | FormData;
}): Promise<void> {
  let body: TableDatumInput | FormData;
  if ("file" in input && input.file instanceof File) {
    body = new FormData();
    body.append("file", input.file);
    for (const [key, value] of Object.entries(input)) {
      if (key !== "file") {
        body.append(key, value);
      }
    }
  } else {
    body = input;
  }
  await fetchQuery(`references/${referenceId}/tables/${tableId}/data/${datumId}`, {
    method: "PUT",
    body: convertToJsonIfNotFormData(body),
  });
}

export async function deleteTableDataAPI({
  referenceId,
  tableId,
  datumId,
}: { referenceId: number; tableId: number; datumId: number }): Promise<void> {
  await fetchQuery(`references/${referenceId}/tables/${tableId}/data/${datumId}`, {
    method: "DELETE",
  });
}

function convertToJsonIfNotFormData(body: TableDatumInput | FormData): string | FormData {
  if (body instanceof FormData) return body;
  return JSON.stringify(Object.fromEntries(body.entries()));
}

function readFileHeaderColumns(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    parseCsv(file, {
      header: false,
      error: (error) => reject(error),
      complete: (results) => {
        resolve((results.data[0] as string[]) || []);
      },
    });
  });
}
